import Strategy from './Strategy.js'
import {mediator} from '../Mediator.js'
import {con, p2p} from '../messages/index.js'
import {jsonRpc} from '../index.js'

import {useEventBus} from '@vueuse/core'
import {watch, type WatchStopHandle} from 'vue'
import {useStreamStore} from '@/stores/streamStore.js'

// The media path can fail while the websocket stays up (NAT rebinding, TURN loss, UDP blocked). 'disconnected' often
// heals by itself and is left alone, 'failed' never does without an ICE restart. After each restart it is checked again
// ('failed' -> 'failed' fires no event), up to ICE_RESTART_MAX restarts (5-10 minutes). Firefox (155) does not send
// media again on a pc that reached 'failed' (20-26 s after 'disconnected'), even after a restart
const ICE_RESTART_RETRY = 10000
const ICE_RESTART_MAX = 30

class P2PStrategy extends Strategy {
  private name: string
  private dataChannel: RTCDataChannel | null
  private screenShareStreamId: string | null
  private pc: RTCPeerConnection | null
  private options: {id: string; party: string} | null
  private remoteAnswers: RTCSessionDescriptionInit[] = []
  private remoteIceCandidates: RTCIceCandidate[] = []
  private iceRestartTimer?: ReturnType<typeof setTimeout>
  private iceRestarts = 0
  private iceErrors: string[] = [] // ICE candidate errors of the current pc, sent with the dump if it fails
  private iceFailureReported = false
  private stopStreamWatch?: WatchStopHandle
  // Messages sent before the data channel opens, flushed in order once it does
  private bufferMessages: string[] = []
  constructor() {
    super()
    this.name = 'p2p'
    this.dataChannel = null
    this.screenShareStreamId = null
    this.pc = null
    this.options = null
    useEventBus('session:participant:joined').on(this.handleJoinedParticipant)
    jsonRpc.on('con:iceServers', this.handleIceServers)
    jsonRpc.on('con:callState', this.onCallStateChanged)
    jsonRpc.on('p2p:offer', this.handleOffer)
    jsonRpc.on('p2p:candidate', this.handleCandidate)
    jsonRpc.on('p2p:answer', this.handleAnswer)
  }

  async initialize() {
    console.info('Initializing P2P strategy')
    // A client announced before our con:strategy (see handleJoinedParticipant)
    const client = this.client()
    if (client) this.handleJoinedParticipant(client)
  }

  client = () => mediator.session?.participants.find(participant => participant.party === 'client')
  assistant = () => mediator.session?.participants.find(participant => participant.party === 'assistant')
  mediaPeerId = () => `${mediator.session?.user.party}-${mediator.session?.user.id}:media`

  user = () => {
    return mediator.session?.user
  }

  handleJoinedParticipant = (participant: {party: string}) => {
    // A client that left or lost its socket was announced by a LOBBY first, which closed its pc. A con:joined while
    // the pc exists (the same socket joined twice, or joins that raced on the server) keeps it: a second pc would
    // send the client a second offer. One that comes before our own con:strategy (a join still in progress, or an
    // SFU call) is left to initialize()
    if (participant.party === 'client' && !this.pc && mediator.strategy === 'p2p') {
      this.pc = this.createPeerConnection('assistant')
      // Also if it never connects: a pc on which no candidate pair forms stays 'new' and reports no state change
      this.scheduleIceRestart(this.pc, ICE_RESTART_RETRY)
    }
  }

  createPeerConnection = (party: 'assistant' | 'client') => {
    this.resetIceRestart()
    this.iceErrors = []
    this.iceFailureReported = false
    const peerId = this.mediaPeerId()
    const {id: counterpartyId} = party === 'assistant' ? this.client() : this.assistant()

    this.options = {
      id: peerId,
      party: counterpartyId,
    }
    const iceServers = mediator.session?.configuration?.iceServers
    const pc = new RTCPeerConnection({
      ...(iceServers ? {iceServers} : {}),
      iceCandidatePoolSize: 5,
      bundlePolicy: 'max-compat',
      rtcpMuxPolicy: 'negotiate', // Options: 'require', 'negotiate'
      iceTransportPolicy: 'all', //Options: 'all', 'relay'
    })
    pc.onicecandidate = this.onIceCandidate
    pc.ontrack = this.onTrack
    pc.ondatachannel = this.onDataChannel
    pc.onconnectionstatechange = e => this.onConnectionStateChange(pc, e)
    pc.onnegotiationneeded = e => this.onRenegotiationNeeded(pc, e)
    pc.onicecandidateerror = this.onIceCandidateError
    this.bufferMessages = []
    this.dataChannel = pc.createDataChannel(party)
    this.dataChannel.onopen = this.flushBuffer
    const streamStore = useStreamStore()
    const {camStream, micStream} = streamStore
    if (camStream) {
      for (const track of camStream.getVideoTracks()) {
        pc.addTrack(track, camStream)
      }
    }
    if (micStream) {
      for (const track of micStream.getAudioTracks()) {
        pc.addTrack(track, micStream)
      }
    }
    this.stopStreamWatch?.()
    this.stopStreamWatch = watch(
      [() => streamStore.camStream, () => streamStore.micStream],
      ([cam, mic], [oldCam, oldMic]) => {
        if (cam !== oldCam) this.syncStream('video', cam)
        if (mic !== oldMic) this.syncStream('audio', mic)
      }
    )
    // The assistant's offer starts the call. It offers to receive every kind it does not send itself (usually video:
    // the assistant has no camera), so the client's camera and microphone are negotiated in this first offer/answer,
    // as before 6e9287d, instead of only in a second negotiation the client has to start once the call is up
    if (party === 'assistant') {
      for (const kind of ['audio', 'video']) {
        if (!pc.getSenders().some(sender => sender.track?.kind === kind)) {
          pc.addTransceiver(kind, {direction: 'recvonly'})
        }
      }
    }
    if (this.remoteAnswers.length > 0) {
      this.remoteAnswers.forEach(async answer => {
        await pc.setRemoteDescription(answer)
      })
      this.remoteAnswers = []
    }
    if (this.remoteIceCandidates.length > 0) {
      this.remoteIceCandidates.forEach(async candidate => {
        await pc.addIceCandidate(candidate)
      })
      this.remoteIceCandidates = []
    }
    return pc
  }

  onIceCandidate = async (event: RTCPeerConnectionIceEvent) => {
    if (event.candidate) {
      jsonRpc.send(p2p.candidateMessage({...this.options, candidate: event.candidate.toJSON()}))
    }
  }

  onTrack = (event: RTCTrackEvent) => {
    const [stream] = event.streams
    const track = event.track
    if (stream.id === this.screenShareStreamId) {
      this.emit('strategy:screensharingStarted:viewer', {stream})
    } else if (track.kind === 'audio') {
      this.emit('strategy:stream:audio', {stream})
    } else {
      this.emit('strategy:stream:video', {stream})
    }
  }

  onMessage = (event: MessageEvent) => {
    const data = JSON.parse(event.data)
    if (data.type === 'screensharing:start') {
      this.screenShareStreamId = data.data.streamId
    }
    this.emit('strategy:data', data)
  }

  onDataChannel = (event: RTCDataChannelEvent) => {
    const dataChannel = event.channel
    dataChannel.onmessage = this.onMessage
  }

  onConnectionStateChange = async (pc: RTCPeerConnection, event: Event) => {
    if (pc.connectionState === 'connected') {
      if (pc === this.pc) this.resetIceRestart()
      await jsonRpc.sendAndWait(con.readyMessage())
      this.emit('strategy:ready', void 0)
    } else if (pc.connectionState === 'failed') {
      this.scheduleIceRestart(pc, 0)
      // Once per pc, after the restart is scheduled: the report must not hold up or break the recovery
      if (pc === this.pc && !this.iceFailureReported) {
        this.iceFailureReported = true
        const candidates = await this.candidateSummary(pc)
        useEventBus('log:dumpTrigger').emit({
          reason: 'p2p-connection-failed',
          error: [candidates, ...this.iceErrors].join(' | '),
        })
      }
    }
  }

  // The candidate types each side gathered (types and protocols only, no addresses): no relay or no srflx candidate
  // points at TURN or STUN, also in a browser that reports no ICE candidate error
  candidateSummary = async (pc: RTCPeerConnection) => {
    try {
      const counts: Record<string, number> = {}
      const stats = await pc.getStats()
      stats.forEach(report => {
        if (report.type === 'local-candidate' || report.type === 'remote-candidate') {
          const side = report.type === 'local-candidate' ? 'local' : 'remote'
          const key = `${side} ${report.candidateType}/${report.relayProtocol ?? report.protocol}`
          counts[key] = (counts[key] ?? 0) + 1
        }
      })
      return `candidates ${JSON.stringify(counts)}`
    } catch {
      return 'candidates unknown' // the pc was closed meanwhile
    }
  }

  // Only the assistant restarts: its offer started the call, and with one initiator the two sides never send crossing
  // restart offers. restartIce() makes onRenegotiationNeeded send an offer with new ICE credentials once the pc is
  // stable, and the client answers it in handleOffer. One timer at a time keeps the restarts ICE_RESTART_RETRY apart
  // whatever the browser reports meanwhile. A websocket drop needs nothing here: mediator.reset() closes the pc, and
  // con:joined after the re-join builds a new one
  scheduleIceRestart = (pc: RTCPeerConnection, delay: number) => {
    if (this.iceRestartTimer || pc !== this.pc || mediator.session?.user.party !== 'assistant') return
    this.iceRestartTimer = setTimeout(() => {
      this.iceRestartTimer = undefined
      const state = pc.connectionState
      if (pc !== this.pc || mediator.strategy !== 'p2p' || state === 'connected' || state === 'closed') return
      if (!this.client()) return // the client left the session (con:disjoined): nobody would answer
      if (this.iceRestarts >= ICE_RESTART_MAX) {
        console.warn(`P2P connection still ${state}, no ICE restart left`)
        return
      }
      // Not again while still 'disconnected': Firefox then reports 'failed' at once and sends no media after 'failed',
      // although the path may still come back. Chromium reaches 'failed' within 15 s and is restarted from there
      if (state === 'disconnected' && this.iceRestarts > 0) {
        this.scheduleIceRestart(pc, ICE_RESTART_RETRY)
        return
      }
      this.iceRestarts++
      // While our last offer is unanswered, restartIce() would only queue another restart behind it
      if (pc.signalingState !== 'have-local-offer') {
        console.warn(`P2P connection ${state}, ICE restart ${this.iceRestarts}/${ICE_RESTART_MAX}`)
        pc.restartIce()
      }
      this.scheduleIceRestart(pc, ICE_RESTART_RETRY)
    }, delay)
  }

  // The health check found the pc connected but no video packet arriving: a new offer re-syncs a negotiation that
  // went wrong (an answer lost, offers that crossed). The assistant offers, as for the ICE restarts; ICE itself works
  renegotiate = () => {
    const pc = this.pc
    if (pc?.signalingState === 'stable' && mediator.session?.user.party === 'assistant') {
      console.warn('P2P connected without video, renegotiating')
      this.onRenegotiationNeeded(pc, new Event('negotiationneeded'))
    }
  }

  resetIceRestart = () => {
    clearTimeout(this.iceRestartTimer)
    this.iceRestartTimer = undefined
    this.iceRestarts = 0
  }

  // Chromium reports a STUN/TURN server it cannot reach from a local interface (701) also on calls that connect, so the
  // errors are only kept and sent with the dump when the connection fails (not every browser fires the event). The
  // server URL and the error, not the local address
  onIceCandidateError = (event: RTCPeerConnectionIceErrorEvent) => {
    const text = `${event.errorCode} ${event.errorText} ${event.url}`
    if (this.iceErrors.length < 10 && !this.iceErrors.includes(text)) {
      this.iceErrors.push(text)
    }
  }
  onRenegotiationNeeded = async (pc: RTCPeerConnection, event: Event) => {
    const offer = await pc.createOffer()
    await pc.setLocalDescription(offer)
    jsonRpc.send(p2p.offerMessage({...this.options, sdp: offer}))
  }

  shareScreen(stream: MediaStream) {
    if (!this.pc) {
      return
    }
    console.log('Sharing screen with track', stream.id)
    const videoTrack = stream.getVideoTracks()[0]
    this.screenShareStreamId = videoTrack.id
    this.pc.addTrack(videoTrack, stream)
  }

  handleOffer = async (event: {detail: {sdp: RTCSessionDescriptionInit}}) => {
    const {sdp} = event.detail
    console.info(`p2p:offer <= ${JSON.stringify(sdp)}`)
    if (!this.pc) {
      this.pc = this.createPeerConnection('client')
    }
    await this.pc.setRemoteDescription(new RTCSessionDescription(sdp))
    const answer = await this.pc.createAnswer()
    await this.pc.setLocalDescription(answer)
    jsonRpc.send(p2p.answerMessage({...this.options, sdp: answer}))
  }

  handleAnswer = async (event: {detail: {sdp: RTCSessionDescriptionInit}}) => {
    const {sdp} = event.detail
    console.info(`p2p:answer <= ${JSON.stringify(sdp)}`)
    const remoteDesc = new RTCSessionDescription(sdp)
    if (this.pc) {
      await this.pc.setRemoteDescription(remoteDesc)
    } else {
      console.warn('PeerConnection not initialized yet, storing remote answer for later')
      this.remoteAnswers.push(remoteDesc)
    }
  }

  handleCandidate = async (event: {detail: {ice: RTCIceCandidateInit}}) => {
    const {ice} = event.detail
    console.info(`p2p:candidate <= ${JSON.stringify(ice)}`)
    const iceCandidate = new RTCIceCandidate(ice)
    if (this.pc) {
      await this.pc.addIceCandidate(iceCandidate)
    } else {
      console.warn('PeerConnection not initialized yet, storing remote candidate for later')
      this.remoteIceCandidates.push(iceCandidate)
    }
  }
  /*
   *
   * Broadcast
   *
   */

  broadcast(data: any) {
    const message = JSON.stringify(data)
    if (this.dataChannel?.readyState === 'open') {
      this.flushBuffer()
      this.dataChannel.send(message)
    } else {
      this.bufferMessages.push(message)
    }
  }

  private flushBuffer = () => {
    const messages = this.bufferMessages.splice(0)
    messages.forEach(message => this.dataChannel?.send(message))
  }

  /*
   *
   * Destroy
   *
   */

  destroy() {
    super.destroy()
    this.destroyPeer()
  }

  destroyPeer() {
    this.resetIceRestart()
    this.stopStreamWatch?.()
    this.stopStreamWatch = undefined
    if (this.dataChannel) {
      this.dataChannel.onmessage = null
      this.dataChannel.onopen = null
      this.dataChannel.close()
    }
    this.dataChannel = null
    this.bufferMessages = []
    if (this.pc) {
      this.pc.onicecandidate = null
      this.pc.ontrack = null
      this.pc.ondatachannel = null
      this.pc.onconnectionstatechange = null
      this.pc.onnegotiationneeded = null
      this.pc.onicecandidateerror = null
      this.pc.close()
    }
    this.pc = null
  }

  destroyScreensharing = () => {
    if (!this.pc) {
      return
    }
    const senders = this.pc.getSenders()
    const sender = senders.find(s => s.track && s.track.id === this.screenShareStreamId)
    if (sender) {
      this.pc.removeTrack(sender)
    }
  }

  /*
   *
   * Tracks
   *
   */

  replaceTrack(type: string, oldTrack: MediaStreamTrack, newTrack: MediaStreamTrack, stream: MediaStream) {
    if (!this.pc) {
      return
    }
    const senders = this.pc.getSenders()
    // sender.track changes only when replaceTrack resolves, so a replacement that overtakes the previous one (or
    // follows one that left the sender elsewhere) finds the sender by kind; the shared screen keeps its own sender
    const sender =
      senders.find(s => s.track && s.track.id === oldTrack.id) ??
      senders.find(s => s.track?.kind === type && s.track.id !== this.screenShareStreamId)
    if (sender) {
      sender.replaceTrack(newTrack)
    } else {
      // The old track was never sent (the connection was created while the kind was being re-acquired)
      this.addTrack(type, newTrack, stream)
    }
  }

  syncStream(type: 'video' | 'audio', stream?: MediaStream | null) {
    if (!this.pc) return
    const track = stream?.getTracks().find(t => t.kind === type && t.readyState === 'live')
    const transceiver = this.pc
      .getTransceivers()
      .find(t => t.receiver.track.kind === type && t.sender.track?.id !== this.screenShareStreamId)
    const sender = transceiver?.sender
    const sends = transceiver?.direction === 'sendrecv' || transceiver?.direction === 'sendonly'
    if (!track) {
      sender?.replaceTrack(null).catch(error => console.warn(`Emptying the ${type} sender failed`, error))
    } else if (sender && sends) {
      sender.replaceTrack(track).catch(error => console.warn(`Replacing the ${type} track failed`, error))
    } else {
      this.pc.addTrack(track, stream!)
    }
  }

  // A kind the connection was created without (blocked or lost when the call started, allowed or recovered
  // mid-call) gets a sender; onnegotiationneeded sends the new offer, as for screen sharing
  addTrack(type: string, track: MediaStreamTrack, stream: MediaStream) {
    if (!this.pc || this.pc.getSenders().some(s => s.track?.kind === type && s.track.id !== this.screenShareStreamId)) {
      return
    }
    this.pc.addTrack(track, stream)
  }

  /*
   *
   * Helpers
   *
   */

  /**
   * Verify if there's any peer (audio or video) is ready. Return true if one of them is ready
   * @returns {boolean}
   */
  isStrategyReady() {
    return this.pc?.connectionState === 'connected'
  }

  async getTotalBits() {
    let totalBits = 0
    if (!this.pc) {
      return totalBits
    }
    const stats = await this.pc.getStats()
    for (const report of stats.values()) {
      if (report.type === 'transport') {
        totalBits += report.bytesSent || 0
        totalBits += report.bytesReceived || 0
      }
    }
    return totalBits
  }

  getMeasureStats = async () => {
    return await this.pc?.getStats()
  }

  onCallStateChanged = async (event: {detail: {state: string}}) => {
    const {state} = event.detail
    console.info(`P2P strategy received call state: ${state}`)
    if (state !== 'IN_CALL') {
      this.destroyPeer()
    }
  }

  handleIceServers = async (event: {detail: {iceServers: RTCIceServer[]}}) => {
    const {iceServers} = event.detail
    this.iceServers = iceServers
  }
}

const p2pStrategy = new P2PStrategy()
export {p2pStrategy}
